import { AlertTriangle } from 'lucide-react';
import type { SimulationSettings, SimulationSetting } from '../../types/simulation';
import { SimulationSettingEnum } from '../../types/simulation';

type SimulationControlsProps = {
  settings: SimulationSettings;
  onSettingChange: (setting: SimulationSetting, value: string) => void;
};

const SETTING_LABELS = {
  [SimulationSettingEnum.SMS_CONNECTION]: 'SMS Connection',
  [SimulationSettingEnum.CODE_VERIFICATION]: 'Code Verification',
  [SimulationSettingEnum.CONTACT_CREATION]: 'Contact Creation',
  [SimulationSettingEnum.APPLICATION_SUBMISSION]: 'Application Submission',
  [SimulationSettingEnum.CONTACT_CENTER]: 'Contact Center Availability',
  [SimulationSettingEnum.CALL_CONNECTION]: 'Connect Call',
  [SimulationSettingEnum.PROSPECT_VERIFY]: 'Prospect Verify',
  [SimulationSettingEnum.EXPERIAN_VERIFICATION]: 'Experian Verification',
  [SimulationSettingEnum.MANUAL_LEAD]: 'Manual Lead',
  [SimulationSettingEnum.KBA_CONNECT]: 'KBA Connect',
  [SimulationSettingEnum.KBA_VERIFY]: 'KBA Verify',
  [SimulationSettingEnum.TCPA_HOURS]: 'TCPA Hours Override',
  [SimulationSettingEnum.WEEKEND]: 'Weekend Override',
  [SimulationSettingEnum.HOLIDAY]: 'Holiday Override',
  [SimulationSettingEnum.BPO_AGENT]: 'BPO Agent Override',
  [SimulationSettingEnum.CALL_INITIATED]: 'Call Initiated State',
  [SimulationSettingEnum.SCHEDULED_TIME]: 'Scheduled Appointment',
  [SimulationSettingEnum.AB_TEST_GROUP]: 'Success Page A/B Test Group'
} as const;

const SETTING_OPTIONS = {
  [SimulationSettingEnum.SMS_CONNECTION]: [
    { value: 'success', label: 'Success' },
    { value: 'user_exists', label: 'User Already Exists' },
    { value: 'invalid_phone', label: 'Invalid Phone Number' },
    { value: 'service_unavailable', label: 'Service Unavailable' },
    { value: 'error_500', label: 'Server Error (500) → Manual Verify' }
  ],
  [SimulationSettingEnum.CONTACT_CENTER]: [
    { value: 'available', label: 'Available (High Capacity)' },
    { value: 'partial', label: 'Available (Low Capacity)' },
    { value: 'unavailable', label: 'Unavailable' }
  ],
  [SimulationSettingEnum.CODE_VERIFICATION]: [
    { value: 'success', label: 'Success' },
    { value: 'invalid_code', label: 'Invalid Code' },
    { value: 'expired_code', label: 'Expired Code' },
    { value: 'too_many_attempts', label: 'Too Many Attempts' },
    { value: 'error_500', label: 'Server Error (500) → Manual Verify' }
  ],
  [SimulationSettingEnum.CONTACT_CREATION]: [
    { value: 'qualified', label: 'Accepted (Tier 1)' },
    { value: 'qualified_tier2', label: 'Accepted (Tier 2)' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'error', label: 'Error' },
    { value: 'error_500', label: 'Server Error (500) → Manual Verify' }
  ],
  [SimulationSettingEnum.APPLICATION_SUBMISSION]: [
    { value: 'success', label: 'Success' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'system_error', label: 'System Error' }
  ],
  [SimulationSettingEnum.CALL_CONNECTION]: [
    { value: 'success', label: 'Success' },
    { value: 'invalid_record', label: 'Invalid Record' },
    { value: 'server_error', label: 'Server Error' }
  ],
  [SimulationSettingEnum.PROSPECT_VERIFY]: [
    { value: 'qualified', label: 'Qualified (200) → Experian' },
    { value: 'unqualified', label: 'Unqualified (200) → Experian' },
    { value: 'validation_error', label: 'Validation Error (400) → Error on page' },
    { value: 'error_500', label: 'Server Error (500) → Error on page' }
  ],
  [SimulationSettingEnum.EXPERIAN_VERIFICATION]: [
    { value: 'qualified', label: 'Qualified → Success' },
    { value: 'qualified_tier2', label: 'Qualified Tier 2 → Success' },
    { value: 'rejected', label: 'Unqualified (422) → Unsuccessful' },
    { value: 'validation_error', label: 'Validation Error (400) → Unsuccessful' },
    { value: 'error_500', label: 'Server Error (500) → Manual Lead Fallback' }
  ],
  [SimulationSettingEnum.MANUAL_LEAD]: [
    { value: 'qualified', label: 'Qualified → Success' },
    { value: 'unqualified', label: 'Unqualified → Unsuccessful' },
    { value: 'validation_error', label: 'Validation Error (400) → Retry / Unsuccessful after 3x' },
    { value: 'not_found', label: 'Not Found (404) → Unsuccessful' }
  ],
  [SimulationSettingEnum.KBA_CONNECT]: [
    { value: 'success', label: 'Success → KBA Questions' },
    { value: 'error_500', label: 'Server Error (500) → Error on page' }
  ],
  [SimulationSettingEnum.KBA_VERIFY]: [
    { value: 'success', label: 'Success → createContact' },
    { value: 'in_progress', label: 'Wrong Answers (IN_PROGRESS) → Refresh questions' },
    { value: 'error_500', label: 'Server Error (500) → Error on page' }
  ],
  [SimulationSettingEnum.TCPA_HOURS]: [
    { value: 'auto', label: 'Auto (Use Real Time)' },
    { value: 'within', label: 'Within TCPA Hours' },
    { value: 'outside', label: 'Outside TCPA Hours' }
  ],
  [SimulationSettingEnum.WEEKEND]: [
    { value: 'auto', label: 'Auto (Use Real Day)' },
    { value: 'no', label: 'Weekday' },
    { value: 'yes', label: 'Weekend' }
  ],
  [SimulationSettingEnum.HOLIDAY]: [
    { value: 'auto', label: 'Auto (Use Real Calendar)' },
    { value: 'no', label: 'Normal Day' },
    { value: 'yes', label: 'Holiday' }
  ],
  [SimulationSettingEnum.BPO_AGENT]: [
    { value: 'auto', label: 'Auto (Use Real Detection)' },
    { value: 'no', label: 'Regular Customer' },
    { value: 'yes', label: 'BPO Agent' }
  ],
  [SimulationSettingEnum.CALL_INITIATED]: [
    { value: 'auto', label: 'Auto (Normal State)' },
    { value: 'no', label: 'Not Initiated' },
    { value: 'yes', label: 'Force Call Initiated' }
  ],
  [SimulationSettingEnum.SCHEDULED_TIME]: [
    { value: 'auto', label: 'Auto (No Appointment)' },
    { value: 'unset', label: 'No Appointment' },
    { value: 'set', label: 'Force Scheduled Appointment' }
  ],
  [SimulationSettingEnum.AB_TEST_GROUP]: [
    { value: 'auto', label: 'Auto (Random 25% split)' },
    { value: 'A', label: 'Group A (30s + schedule button)' },
    { value: 'B', label: 'Group B (60s + missed call)' },
    { value: 'C', label: 'Group C (30s + missed call)' },
    { value: 'D', label: 'Group D (click-to-call only)' }
  ]
} as const;

export default function SimulationControls({ settings, onSettingChange }: SimulationControlsProps) {
  return (
    <div className="space-y-6">
      <div className="bg-yellow-50 p-4 rounded-lg flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-yellow-600">
          <p className="font-medium mb-1">Simulation Mode Active</p>
          <p>These settings only affect the mock services and are intended for testing different scenarios.</p>
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(SETTING_LABELS).map(([setting, label]) => (
          <div key={setting} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {label}
            </label>
            <select
              value={settings[setting as SimulationSetting]}
              onChange={(e) => onSettingChange(setting as SimulationSetting, e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#212d52] focus:border-[#212d52] sm:text-sm"
            >
              {SETTING_OPTIONS[setting as SimulationSetting].map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
